import { safeJsonObject } from '../lib/safeJson'
import { useRef, useState } from 'react'

import type { ColumnInfo } from '../api/client'
import { parseFormula } from './formula'

/**
 * M9.6：公式列的设置对话框（列菜单 → 编辑公式）。
 *
 * 只负责编辑 settings 里的 `formula` 字符串；保存时把整个 settings 覆盖回去
 * （与后端 `PATCH /api/boards/:id/columns` 的 `settings` 字段一致）。
 */

export interface FormulaEditorProps {
  column: ColumnInfo
  columns: ColumnInfo[]
  busy: boolean
  onCancel: () => void
  onSave: (settingsJson: string) => void
}

function readFormula(column: ColumnInfo): string {
  const raw = column.settings as unknown
  if (raw !== null && typeof raw === 'object') {
    const f = (raw as { formula?: unknown }).formula
    return typeof f === 'string' ? f : ''
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = safeJsonObject<{ formula?: unknown }>(raw, {}, 'formula')
    return typeof parsed.formula === 'string' ? parsed.formula : ''
  }
  return ''
}

export default function FormulaEditor({ column, columns, busy, onCancel, onSave }: FormulaEditorProps) {
  const [text, setText] = useState(() => readFormula(column))
  const taRef = useRef<HTMLTextAreaElement>(null)	// 用于「在光标处插入引用」
  const parsed = parseFormula(text)

  const refs = columns.filter((c) => c.key !== column.key && c.title.trim() !== '')

  return (
    <div className="dialog-mask" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel() }}>
      <div className="dialog formula-editor" onClick={(e) => e.stopPropagation()}>
        <div className="dialog__title">设置公式 —— {column.title}</div>
        <div className="formula-body">
          <textarea
            ref={taRef}
            className={'formula-input' + (parsed.ok ? '' : ' formula-input--bad')}
            value={text}
            spellCheck={false}
            placeholder='例如：{单价} * {数量}'
            onChange={(e) => setText(e.target.value)}
            rows={3}
          />
          <div className={parsed.ok ? 'formula-hint' : 'formula-hint formula-hint--bad'}>
            {parsed.ok ? '语法正确' : `语法错误：${parsed.error ?? '未知'}`}
          </div>
          <div className="formula-help">
            <div>引用列：用 {'{标题}'}，例如 <code>{`{单价}`}</code>。找不到时按列 key 兜底。</div>
            <div>运算符：<code>+ - * / ( )</code></div>
            <div>函数：<code>SUM/AVG/MIN/MAX/COUNT({'列'})</code> 整列聚合（忽略空值）、<code>ROUND(x,n)</code>、<code>ABS(x)</code>、<code>DATEDIF({'{开始}'},{'{结束}'},"d")</code></div>
            <div>空值参与运算结果仍为空；错误显示 <code>#REF!</code> / <code>#DIV/0!</code> / <code>#CYCLE!</code></div>
          </div>
          <div className="formula-refs">
            可引用：
            {refs.map((c) => (
              <button
                key={c.key}
                type="button"
                className="formula-ref"
                onClick={() => {
                  // 在**光标位置**插入，而不是总是追加到末尾；插入后把光标放到引用之后
                  const ta = taRef.current
                  const insert = `{${c.title}}`
                  const start = ta?.selectionStart ?? text.length
                  const end = ta?.selectionEnd ?? start
                  const next = text.slice(0, start) + insert + text.slice(end)
                  setText(next)
                  if (ta !== null) {
                    const pos = start + insert.length
                    requestAnimationFrame(() => {
                      ta.focus()
                      ta.setSelectionRange(pos, pos)
                    })
                  }
                }}
              >
                {c.title}
              </button>
            ))}
          </div>
        </div>
        <div className="dialog__actions">
          <button type="button" className="dialog__btn" disabled={busy} onClick={onCancel}>
            取消
          </button>
          <button
            type="button"
            className="dialog__btn dialog__btn--primary"
            disabled={busy || !parsed.ok}
            onClick={() => onSave(JSON.stringify({ formula: text }))}
          >
            保存
          </button>
        </div>
      </div>
    </div>
  )
}
